import { useLingui } from '@lingui/react';
import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from '@lingui/core';
import { AlertTriangle, BellRing, Mail, type LucideIcon } from 'lucide-react';

import { StatusBadge, type StatusTone } from '@/components/ui/status-badge';

interface EmailKindBadgeProps {
  kind: string | undefined;
}

/** The reminder stages escalate in tone: an invoice is neutral, a final notice is danger. */
const KIND: Record<string, { tone: StatusTone; icon: LucideIcon; label: MessageDescriptor }> = {
  invoice_send: {
    tone: 'neutral',
    icon: Mail,
    label: msg({ id: 'email-cockpit.kind.option.invoice', message: 'Invoice' }),
  },
  dunning_reminder_1: {
    tone: 'info',
    icon: BellRing,
    label: msg({ id: 'email-cockpit.kind.option.dunning-r1', message: 'Dunning - R1' }),
  },
  dunning_reminder_2: {
    tone: 'warning',
    icon: BellRing,
    label: msg({ id: 'email-cockpit.kind.option.dunning-r2', message: 'Dunning - R2' }),
  },
  dunning_reminder_3: {
    tone: 'warning',
    icon: BellRing,
    label: msg({ id: 'email-cockpit.kind.option.dunning-r3', message: 'Dunning - R3' }),
  },
  dunning_final_notice: {
    tone: 'danger',
    icon: AlertTriangle,
    label: msg({ id: 'email-cockpit.kind.option.dunning-final', message: 'Dunning - Final' }),
  },
  dunning_final_repeat: {
    tone: 'danger',
    icon: AlertTriangle,
    label: msg({ id: 'email-cockpit.kind.option.dunning-final-repeat', message: 'Dunning - Final repeat' }),
  },
};

export function EmailKindBadge({ kind }: EmailKindBadgeProps) {
  const { i18n } = useLingui();
  if (!kind) {
    return <span className="text-muted-foreground">—</span>;
  }
  const config = KIND[kind];
  return (
    <span data-testid={`email-kind-${kind}`} data-tone={config?.tone ?? 'neutral'} className="inline-flex">
      <StatusBadge
        tone={config?.tone ?? 'neutral'}
        icon={config?.icon ?? Mail}
        label={config ? i18n._(config.label) : kind}
      />
    </span>
  );
}
